import React, {
  createContext,
  ReactNode,
  useContext,
  useState,
  useRef,
  useCallback,
} from "react";
import classNames from "classnames";
import { Slot } from "@radix-ui/react-slot";
import {
  FOCUS_BLOCK_PLACEMENT,
  FOCUS_BLOCK_PLACEMENT_TYPE,
} from "constants/focusBlock";

/* -------------------------------------------------------------------------------------------------
 * Focus Block Context (Root-level)
 * -----------------------------------------------------------------------------------------------*/

interface FocusBlockContextValue {
  value: string | null;
  onValueChange: (val: string | null) => void;
}

const FocusBlockContext = createContext<FocusBlockContextValue | null>(null);

export const useFocusBlockContext = () => {
  const ctx = useContext(FocusBlockContext);
  if (!ctx)
    throw new Error("useFocusBlock must be used within <FocusBlock.Root>");
  return ctx;
};

/* -------------------------------------------------------------------------------------------------
 * Root
 * -----------------------------------------------------------------------------------------------*/

interface RootProps {
  children: ReactNode;
  className?: string;
  value?: string | null;
  onValueChange?: (val: string | null) => void;
  defaultValue?: string | null;
}

const Root: React.FC<RootProps> = ({
  children,
  className = "",
  value: controlledValue,
  onValueChange,
  defaultValue = null,
}) => {
  const [uncontrolledValue, setUncontrolledValue] = useState<string | null>(
    defaultValue
  );

  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : uncontrolledValue;

  const handleValueChange = useCallback(
    (val: string | null) => {
      onValueChange?.(val);
      setUncontrolledValue(val);
    },
    [isControlled, onValueChange]
  );

  return (
    <FocusBlockContext.Provider
      value={{ value, onValueChange: handleValueChange }}
    >
      <div className="t-relative">
        {value && (
          <div
            className={classNames(
              "t-absolute t-inset-0 t-bg-white t-bg-opacity-85 t-z-40 pointer-events-none",
              className
            )}
          />
        )}
        {children}
      </div>
    </FocusBlockContext.Provider>
  );
};

/* -------------------------------------------------------------------------------------------------
 * Item Context (Per-item level)
 * -----------------------------------------------------------------------------------------------*/

interface ItemContextValue {
  value: string;
  isActive: boolean;
  open: () => void;
  close: () => void;
  placement: FOCUS_BLOCK_PLACEMENT_TYPE;
}

const ItemContext = createContext<ItemContextValue | null>(null);

export const useItemContext = () => {
  const ctx = useContext(ItemContext);
  if (!ctx) throw new Error("useItem must be used within <FocusBlock.Item>");
  return ctx;
};

/* -------------------------------------------------------------------------------------------------
 * Item
 * -----------------------------------------------------------------------------------------------*/

interface ItemProps {
  value: string;
  children: ReactNode;
  placement?: FOCUS_BLOCK_PLACEMENT_TYPE;
}

const Item: React.FC<ItemProps> = ({
  value: itemValue,
  children,
  placement = FOCUS_BLOCK_PLACEMENT.INSERT,
}) => {
  const { value, onValueChange } = useFocusBlockContext();
  const isActive = value === itemValue;

  const open = useCallback(
    () => onValueChange(itemValue),
    [itemValue, onValueChange]
  );
  const close = useCallback(() => onValueChange(null), [onValueChange]);

  const contextValue: ItemContextValue = {
    value: itemValue,
    isActive,
    open,
    close,
    placement,
  };

  const childrenArray = React.Children.toArray(children);

  const content = childrenArray.find(
    (child) => React.isValidElement(child) && child.type === Content
  );

  return (
    <ItemContext.Provider value={contextValue}>
      {placement === FOCUS_BLOCK_PLACEMENT.INSERT && <>{childrenArray}</>}

      {placement === FOCUS_BLOCK_PLACEMENT.REPLACE && (
        <>{value && isActive ? content : childrenArray}</>
      )}
    </ItemContext.Provider>
  );
};

/* -------------------------------------------------------------------------------------------------
 * Trigger
 * -----------------------------------------------------------------------------------------------*/

type TriggerProps = React.HTMLAttributes<HTMLButtonElement> & {
  asChild?: boolean;
};

const Trigger: React.FC<TriggerProps> = (props: TriggerProps) => {
  const { open } = useItemContext();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    open();
  };

  const Component = props.asChild ? Slot : "button";

  return (
    <Component onClick={handleClick} {...props}>
      {props.children}
    </Component>
  );
};

/* -------------------------------------------------------------------------------------------------
 * Content
 * -----------------------------------------------------------------------------------------------*/

type ContentProps = {
  asChild?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

const Content: React.FC<ContentProps> = ({ asChild, ...props }) => {
  const { value } = useFocusBlockContext();
  const { isActive, placement } = useItemContext();

  if (!value || !isActive) return null;

  const Component = asChild ? Slot : "div";

  return (
    <Component
      className={classNames(
        "t-isolate t-relative t-z-bg-overlay t-bg-white",
        placement === FOCUS_BLOCK_PLACEMENT.INSERT && "t-mb-3"
      )}
    >
      {props.children}
    </Component>
  );
};

/* -------------------------------------------------------------------------------------------------
 * Exported API
 * -----------------------------------------------------------------------------------------------*/

const FocusBlock = {
  Root,
  Item,
  Trigger,
  Content,
};

export default FocusBlock;
